import { getAIProvider, getOpenAIKey, getOpenRouterKey, getAIModel, getAIFastModel, getAIVisionModel, getAITitle } from "utils/storage.ts";

export interface AIClientConfigState {
  provider: "openai" | "openrouter" | null;
  apiKey: string | null;
  model: string | null;
  fastModel: string | null;
  visionModel: string | null;
  title: string | null;
}

export function getAIConfig(): AIClientConfigState {
  const provider = getAIProvider();
  const model = getAIModel();
  const fastModel = getAIFastModel();
  const visionModel = getAIVisionModel();
  const title = getAITitle();
  const apiKey = provider === "openai" ? getOpenAIKey() : provider === "openrouter" ? getOpenRouterKey() : null;
  return { provider, apiKey, model, fastModel, visionModel, title };
}

export function isAIConfigured(): boolean {
  const { provider, apiKey } = getAIConfig();
  return !!provider && !!apiKey;
}

export async function requestChatCompletion(prompt: string, options?: { model?: string }): Promise<string> {
  const { provider, apiKey, model: defaultModel, title } = getAIConfig();
  if (!provider || !apiKey) {
    throw new Error("AI provider/key not configured");
  }

  const baseUrl = provider === "openai" ? "https://api.openai.com/v1" : "https://openrouter.ai/api/v1";
  const selectedModel = options?.model ?? defaultModel ?? (provider === "openai" ? "gpt-4o-mini" : "openrouter/auto");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiKey}`,
  };
  if (provider === "openrouter" && title) headers["X-Title"] = title;

  const messages = [
    { role: "system", content: "You are a helpful culinary assistant that proposes healthy dinner recipes." },
    { role: "user", content: prompt },
  ];

  const res = await fetch(`${baseUrl}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify({ model: selectedModel, messages }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`AI request failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  const content = data?.choices?.[0]?.message?.content;
  if (typeof content !== "string") {
    throw new Error("AI response missing message content");
  }
  return content;
}

export async function requestClassification(prompt: string): Promise<string> {
  const { provider, apiKey, fastModel, title } = getAIConfig();
  if (!provider || !apiKey) {
    throw new Error("AI provider/key not configured");
  }

  const baseUrl = provider === "openai" ? "https://api.openai.com/v1" : "https://openrouter.ai/api/v1";
  const selectedModel = fastModel ?? (provider === "openai" ? "gpt-4o-mini" : "openrouter/auto");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiKey}`,
  };
  if (provider === "openrouter" && title) headers["X-Title"] = title;

  const messages = [
    { role: "system", content: "You are a helpful assistant for classification tasks." },
    { role: "user", content: prompt },
  ];

  const res = await fetch(`${baseUrl}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify({ model: selectedModel, messages}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`AI request failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  const content = data?.choices?.[0]?.message?.content;
  if (typeof content !== "string") {
    throw new Error("AI response missing message content");
  }
  return content;
}

interface VisionCompletionOptions {
  systemPrompt: string;
  prompt: string;
  imageDataUrl: string;
  model?: string;
}

export async function requestVisionCompletion(options: VisionCompletionOptions): Promise<string> {
  const { provider, apiKey, model: defaultModel, visionModel, title } = getAIConfig();
  if (!provider || !apiKey) {
    throw new Error("AI provider/key not configured");
  }

  const baseUrl = provider === "openai" ? "https://api.openai.com/v1" : "https://openrouter.ai/api/v1";
  const selectedModel = options.model ?? visionModel ?? defaultModel ?? (provider === "openai" ? "gpt-4o-mini" : "openrouter/auto");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiKey}`,
  };
  if (provider === "openrouter" && title) headers["X-Title"] = title;

  const messages = [
    { role: "system", content: options.systemPrompt },
    {
      role: "user",
      content: [
        { type: "text", text: options.prompt },
        { type: "image_url", image_url: { url: options.imageDataUrl } },
      ],
    },
  ];

  const res = await fetch(`${baseUrl}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify({ model: selectedModel, messages }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`AI vision request failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  const content = data?.choices?.[0]?.message?.content;
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    const joined = content
      .map((item) => item?.type === "text" && typeof item.text === "string" ? item.text : "")
      .filter(Boolean)
      .join("");
    if (joined) return joined;
  }
  throw new Error("AI response missing message content");
}

export async function requestChatCompletionStream(
  prompt: string,
  handlers: {
    onToken: (token: string, accumulated: string) => void;
    onReasoning?: (token: string, accumulated: string, details?: unknown) => void;
    onError?: (err: unknown) => void;
    onDone?: () => void;
    signal?: AbortSignal;
  },
  options?: { model?: string }
): Promise<void> {
  const { provider, apiKey, model: defaultModel, title } = getAIConfig();
  if (!provider || !apiKey) {
    throw new Error("AI provider/key not configured");
  }

  const baseUrl = provider === "openai" ? "https://api.openai.com/v1" : "https://openrouter.ai/api/v1";
  const selectedModel = options?.model ?? defaultModel ?? (provider === "openai" ? "gpt-4o-mini" : "openrouter/auto");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiKey}`,
    Accept: "text/event-stream",
  };
  if (provider === "openrouter" && title) headers["X-Title"] = title;

  const messages = [
    { role: "system", content: "You are a helpful culinary assistant that proposes healthy dinner recipes." },
    { role: "user", content: prompt },
  ];

  const res = await fetch(`${baseUrl}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify({ model: selectedModel, messages, stream: true }),
    signal: handlers.signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`AI request failed: ${res.status} ${text}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let accumulatedText = "";
  let accumulatedReasoning = "";
  const emitToken = (t: string) => {
    if (!t) return;
    accumulatedText += t;
    handlers.onToken(t, accumulatedText);
  };
  const emitReasoning = (t: string, details?: unknown) => {
    if (!t || !handlers.onReasoning) return;
    accumulatedReasoning += t;
    handlers.onReasoning(t, accumulatedReasoning, details);
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Process Server-Sent Events frames separated by double newlines
      let separatorIndex: number;
      while ((separatorIndex = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);

        // Each event can have multiple lines; we're interested in lines starting with 'data:'
        const lines = rawEvent.split(/\r?\n/);
        for (const line of lines) {
          const trimmed = line.trimStart();
          if (!trimmed.startsWith("data:")) continue;
          const dataStr = trimmed.slice(5).trim();
          if (!dataStr) continue;
          if (dataStr === "[DONE]") {
            if (handlers.onDone) handlers.onDone();
            return;
          }
          try {
            const obj = JSON.parse(dataStr);
            // Extract standard content token
            const token =
              obj?.choices?.[0]?.delta?.content ??
              obj?.choices?.[0]?.message?.content ??
              obj?.delta?.content ??
              obj?.content ??
              "";
            if (typeof token === "string") emitToken(token);
            // Extract optional reasoning traces
            const reasoningToken: unknown = obj?.choices?.[0]?.delta?.reasoning;
            const reasoningDetails: unknown = obj?.choices?.[0]?.delta?.reasoning_details;
            if (typeof reasoningToken === "string") {
              emitReasoning(reasoningToken, reasoningDetails);
            } else if (
              Array.isArray(reasoningDetails) && reasoningDetails.length > 0
            ) {
              // Some providers send summaries/chunks in details; try to surface a summary field if present
              const summaries = reasoningDetails
                .map((d: unknown) => (d && typeof d === 'object' && 'summary' in (d as Record<string, unknown>) ? String((d as Record<string, unknown>).summary) : ''))
                .filter(Boolean)
                .join(' ');
              if (summaries) emitReasoning(summaries, reasoningDetails);
            }
          } catch (_e) {
            // Not JSON or partial line; ignore
          }
        }
      }
    }
    if (buffer.length > 0) {
      // Process any trailing data without the ending separator
      const lines = buffer.split(/\r?\n/);
      for (const line of lines) {
        const trimmed = line.trimStart();
        if (!trimmed.startsWith("data:")) continue;
        const dataStr = trimmed.slice(5).trim();
        if (!dataStr || dataStr === "[DONE]") continue;
        try {
          const obj = JSON.parse(dataStr);
          const token =
            obj?.choices?.[0]?.delta?.content ??
            obj?.choices?.[0]?.message?.content ??
            obj?.delta?.content ??
            obj?.content ??
            "";
          if (typeof token === "string") emitToken(token);
          const reasoningToken: unknown = obj?.choices?.[0]?.delta?.reasoning;
          const reasoningDetails: unknown = obj?.choices?.[0]?.delta?.reasoning_details;
          if (typeof reasoningToken === "string") {
            emitReasoning(reasoningToken, reasoningDetails);
          }
        } catch (_e) {
          // ignore
        }
      }
    }
    if (handlers.onDone) handlers.onDone();
  } catch (e) {
    if (handlers.onError) handlers.onError(e);
    else throw e;
  }
}
